/**
 * Useful components to help with related articles:
 *
 * - Group
 * - Loop
 * - Count
 *
 * @module
 */
import * as React from 'react';
import { ArticleFragmentContext } from '../contexts';
import { useRelatedArticles } from '../hooks';
const isGroupFunction = (arg) => {
    return typeof arg === 'function';
};
function returnChild(children, data) {
    return (React.createElement(React.Fragment, null, React.isValidElement(children)
        ? children
        : isGroupFunction(children)
            ? children(data)
            : null));
}
/**
 * Returns a Related Articles grouped in JSX Fragment.
 */
export const Group = ({ children }) => {
    const relatedArticlesData = useRelatedArticles();
    return React.createElement(React.Fragment, null, returnChild(children, relatedArticlesData));
};
Group.displayName = 'RelatedArticles.Group';
const isArticleLoopFunction = (arg) => {
    return typeof arg === 'function';
};
/**
 * Checks the type of the provided React hild and returns it in the correct way.
 */
function returnLoopChild(children, data) {
    return React.isValidElement(children)
        ? children
        : isArticleLoopFunction(children)
            ? children(data)
            : null;
}
/**
 * Repeats the children passed in for each of the articles realted to it.
 *
 * - If you pass a render prop as a child, it will be called with each sub category result.
 * - Else if you pass in React Elements, they will be repeated for each sub category result.
 */
export const Loop = ({ children }) => {
    const relatedArticles = useRelatedArticles();
    if (!relatedArticles)
        return null;
    return (React.createElement(React.Fragment, null, relatedArticles.map((article, index) => (React.createElement(ArticleFragmentContext.Provider, { key: article.id, value: article.id }, returnLoopChild(children, {
        article,
        index,
    }))))));
};
Loop.displayName = 'RelatedArticles.Loop';
/**
 * Either prints out or passes (in the case of a render prop as children) the total number of related articles.
 *
 * Utilises [[useRelatedArticles]] under the hood.
 *
 * - If no Related Articles, returns `null`
 */
export const Count = ({ children }) => {
    const relatedArticles = useRelatedArticles();
    if (!relatedArticles)
        return null;
    const count = relatedArticles.length;
    if (children)
        return React.createElement(React.Fragment, null, children({ count }));
    return React.createElement(React.Fragment, null, count);
};
Count.displayName = 'RelatedArticles.Count';
//# sourceMappingURL=relatedArticles.js.map